import type React from 'react';

import { useGetGroups, useGetSelf } from '@vlabs/api/artemy/client';
import { withPermissions, useHasPermission } from '@vlabs/permissions';

import { AttachPermissionTagGroupButton } from '@vlabs/monokit/features/attach-permission-tag-group';

import { usePermissionTagStreamGroupListInfoTranslation } from './i18n';
import { PermissionTagGroupListItem } from './PermissionTagGroupListItem';
import st from './PermissionTagStreamGroupListInfo.module.sass';
import type { PermissionTagStreamGroupListInfoProps } from './types';
import { DetachPermissionTagGroupButton } from '../../features/detach-permission-tag-group';
import { FoldableEntity } from '../../shared/ui/foldable-entity';

const AttachGroupWithAccess = withPermissions('group', 'update')(AttachPermissionTagGroupButton);
const DetachGroupWithAccess = withPermissions('group', 'update')(DetachPermissionTagGroupButton);

const GROUPS_PAGE_SIZE = 1000;

function PermissionTagStreamGroupListInfo({
  permissionTag,
}: PermissionTagStreamGroupListInfoProps): React.JSX.Element {
  const { t } = usePermissionTagStreamGroupListInfoTranslation();
  const getSelf = useGetSelf();

  const departmentIds = getSelf.data?.department_ids ?? [];
  const canViewGroups = useHasPermission('group', 'read');
  const getGroupsDetails = useGetGroups({
    page_size: GROUPS_PAGE_SIZE,
    permission_tags: [permissionTag],
  });

  if (!canViewGroups) return null;

  const groups = getGroupsDetails.data?.groups ?? [];

  return (
    <FoldableEntity
      actions={<AttachGroupWithAccess permissionTag={permissionTag} />}
      heading={t('title')}
    >
      <div className={st.List}>
        {!!getGroupsDetails.isError && (
          <div className={st.State}>
            {t('error')}
          </div>
        )}
        {!!getGroupsDetails.isSuccess && groups.length === 0 && (
          <div className={st.State}>
            {t('empty')}
          </div>
        )}

        {!!getGroupsDetails.isSuccess && groups.map(({ group_id: groupId, owner_department }) => {
          const belongsToUserDepartment = departmentIds.includes(owner_department);
          return (
            <PermissionTagGroupListItem
              actions={(
                <DetachGroupWithAccess
                  disabled={!belongsToUserDepartment}
                  groupId={groupId}
                  title={belongsToUserDepartment ? undefined : t('detach_disabled')}
                />
              )}
              groupId={groupId}
              key={groupId}
            />
          );
        })}
      </div>
    </FoldableEntity>
  );
}

export { PermissionTagStreamGroupListInfo };
